import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SectionHeading, Reveal } from "@/components/Reveal";
import { TiltCard } from "@/components/TiltCard";
import { scrollToId } from "@/components/Navbar";
import { SERVICE_CATEGORIES } from "@/data/content";

export const ServicesSection = () => {
  return (
    <section id="services" className="py-16 sm:py-20 lg:py-24" data-testid="services-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="What We Do"
          title="One consultancy. Every IT capability."
          description="Sixteen service lines across development, commerce, infrastructure and intelligent systems — so you never need a second supplier."
        />

        <div className="mt-12 space-y-14">
          {SERVICE_CATEGORIES.map((category, ci) => (
            <div key={category.id} data-testid={`services-category-${category.id}`}>
              <Reveal className="flex items-baseline justify-between flex-wrap gap-2 mb-6">
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold tracking-tight text-foreground">
                    {category.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">{category.tagline}</p>
                </div>
                <span className="text-xs font-mono-meta text-muted-foreground">
                  0{ci + 1} / 04
                </span>
              </Reveal>
              <div className="section-accent-line mb-6" />

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
                {category.services.map((service, si) => {
                  const Icon = service.icon;
                  return (
                    <motion.div
                      key={service.title}
                      initial={{ opacity: 0, y: 22 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ duration: 0.55, delay: si * 0.08, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <TiltCard
                        className="card-highlight h-full rounded-2xl bg-card/80 border border-border backdrop-blur-md p-5 hover:border-primary/35 hover:shadow-[0_18px_60px_rgba(0,0,0,0.45)] transition-[border-color,box-shadow] duration-300"
                        data-testid="service-card"
                      >
                        <div className="w-11 h-11 rounded-xl bg-secondary grid place-items-center mb-4 border border-border">
                          <Icon className="w-5 h-5 text-primary" />
                        </div>
                        <h4 className="text-base font-semibold text-foreground mb-2">
                          {service.title}
                        </h4>
                        <p className="text-sm leading-relaxed text-muted-foreground mb-4">
                          {service.description}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {service.tags.map((tag) => (
                            <Badge
                              key={tag}
                              variant="outline"
                              className="border-border text-muted-foreground text-[11px] rounded-full"
                            >
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </TiltCard>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <Reveal className="mt-14 flex justify-center">
          <Button
            size="lg"
            onClick={() => scrollToId("contact")}
            className="cta-shimmer bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-xl px-8 h-12 hover:-translate-y-px transition-transform duration-200"
            data-testid="services-contact-cta-button"
          >
            Discuss Your Requirements <ArrowUpRight className="ml-2 h-4 w-4" />
          </Button>
        </Reveal>
      </div>
    </section>
  );
};
